import { haversineKm } from './bulkImportClustering';

// Deliberately well above clusterPhotos' own GPS_DISTANCE_KM=2 (a same-day
// cluster-boundary threshold), so a normal day of city photos doesn't
// false-positive as a trip.
export const TRIP_MIN_SPAN_DAYS = 2;
export const TRIP_MIN_GPS_SPREAD_KM = 25;
export const REUNION_MIN_PARTICIPANTS = 6;

export interface ClusterTemplateKindInput {
  photos: { takenAt: Date | null; gps: { lat: number; lng: number } | null }[];
  participantNames: string[];
}

export type ClusterKindSuggestion = { kind: 'trip' | 'reunion'; reason: string } | null;

/**
 * Heuristic-only cluster-kind suggestion — reused signals from
 * bulkImportClustering.ts, no ML/LLM. Birthday/Wedding/Graduation/Memorial are
 * never returned: no reliable non-visual signal exists for them. No dated
 * photos -> null (never guess, same principle as clusterPhotos/findSuggestedEventMatch).
 */
export function suggestClusterTemplateKind(input: ClusterTemplateKindInput): ClusterKindSuggestion {
  const dated = input.photos.filter((p): p is { takenAt: Date; gps: { lat: number; lng: number } | null } => p.takenAt !== null);
  if (dated.length === 0) return null;

  const times = dated.map(p => p.takenAt.getTime());
  const spanDays = (Math.max(...times) - Math.min(...times)) / (1000 * 60 * 60 * 24);

  const gpsPoints = dated.filter(p => p.gps).map(p => p.gps!);
  let gpsSpreadKm = 0;
  for (let i = 0; i < gpsPoints.length; i++) {
    for (let j = i + 1; j < gpsPoints.length; j++) {
      gpsSpreadKm = Math.max(gpsSpreadKm, haversineKm(gpsPoints[i], gpsPoints[j]));
    }
  }

  if (spanDays >= TRIP_MIN_SPAN_DAYS || gpsSpreadKm >= TRIP_MIN_GPS_SPREAD_KM) {
    return { kind: 'trip', reason: spanDays >= TRIP_MIN_SPAN_DAYS ? 'multi-day' : 'gps-spread' };
  }

  const distinctParticipants = new Set(input.participantNames).size;
  if (distinctParticipants >= REUNION_MIN_PARTICIPANTS) {
    return { kind: 'reunion', reason: 'many-participants' };
  }

  return null;
}
